import { BRAND } from '../../config/brand'
/**
 * How a recorded plan change reads out loud (SC-13, SC-3, SC-36).
 *
 * The backend writes an append-only log of what it did to a day's plan:
 * `{trigger_event, previous_item, replacement_item, reason_code,
 * policy_inputs, displaced_to, reconciled_at}` — machine symbols,
 * coordinates and the user's own titles, deliberately with no prose in it.
 * This module is the other half of that split: ONE fixed phrase per
 * canonical symbol, looked up.
 *
 * The rule that matters here is what this module may NOT do (SC-3):
 *   - it may not compose an explanation out of several fields ("moved to
 *     14:00 because your deadline came forward") — a sentence the client
 *     assembles can drift from what the engine actually recorded, and the
 *     engine is the only thing that knows,
 *   - it may not render a symbol it does not recognise. An unmapped
 *     `reason_code` renders NOTHING rather than leaking `superseded_by_
 *     replan` at a human (SC-36) or being guessed at,
 *   - it may not say anything about items the row did not carry.
 *
 * Everything it does render is either a phrase from a table below or a
 * fact the endpoint returned verbatim (a title, a time slot, a date).
 */

import type { PlanChangeItem } from '../../services/scheduleService'

/**
 * Why THIS item changed — `schedule_change_log.REASON_CODES`, one phrase
 * each, in the Flexible/Confirmed/Later register (SC-36).
 *
 * Two things every line here holds to: it never blames the user for
 * Horton's own replanning (SC-17 — all but one of these codes are
 * bookkeeping), and it never claims a judgement the engine did not make
 * ("a better time" is a verdict; "another time" is what happened).
 */
export const REASON_COPY: Record<string, string> = {
  demoted_to_flexible:
    `This one is flexible again — ${BRAND.assistant} can move it if the day needs it.`,
  replaced_by_new_proposal: `${BRAND.assistant} put something else in this time.`,
  superseded_by_replan: `${BRAND.assistant} re-planned the day, so this time changed.`,
  moved: `${BRAND.assistant} moved this to another time.`,
  displaced: 'Something more urgent needed this time.',
  expired_unattempted: `That time came and went, so ${BRAND.assistant} let it go. Nothing is lost.`,
  kept_free: `${BRAND.assistant} left this time free on purpose.`,
}

/**
 * What set the change off — `schedule_change_log.TRIGGER_EVENTS`.
 *
 * Only the triggers that tell the user something the reason code does not
 * are voiced. `replan` and `hold_decay` are deliberately absent: their
 * reason lines already say exactly that, and printing both would be the
 * same fact twice in two voices.
 */
export const TRIGGER_COPY: Record<string, string> = {
  rebalance_requested: `After you asked ${BRAND.assistant} to re-plan.`,
  ai_assist_placement: `While ${BRAND.assistant} was finding times for your work.`,
  deadline_changed: 'After a deadline moved.',
  calendar_synced: 'After your calendar changed.',
  // "hold" is the backend's word for a Flexible block, so it stays out of
  // user copy even in its ordinary English sense (SC-36) — one page cannot
  // teach the reader two meanings of the same word.
  energy_changed: 'After you changed how much today should carry.',
}

/**
 * Where the displaced work went.
 *
 * `unscheduled` and `parked` are the two sentinels the log defines; any
 * other non-empty value is a TEXT ref to the block it landed on. We cannot
 * name that block (the row carries a ref, not a title), so the phrase says
 * only what the ref itself entails — it has a place in the plan — and
 * never invents where.
 */
export function displacedCopy(displacedTo: string | null): string | null {
  if (!displacedTo) return null
  if (displacedTo === 'unscheduled') return 'It went back to your list, with no time set.'
  if (displacedTo === 'parked') return 'It is set aside for later.'
  return 'It has another time in your plan.'
}

/**
 * One item as a single line of facts: the user's title, its time slot, and
 * its date when that is NOT the day being explained (a move can land on
 * another day, and "moved" would otherwise read as "moved within today").
 *
 * Duration only appears when there is no time slot to show — a slot
 * already carries the length, and printing both reads as two claims.
 */
export function itemLine(
  item: PlanChangeItem | null,
  surfaceDate: string,
): string | null {
  if (!item) return null
  const parts: string[] = []
  if (item.title) parts.push(item.title)
  if (item.timeSlot) parts.push(item.timeSlot)
  else if (item.durationMinutes) parts.push(`${item.durationMinutes}m`)
  if (item.date && item.date !== surfaceDate) parts.push(item.date)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * True when the two sides of a change sit at different coordinates.
 *
 * A demotion records the same block twice — same title, same slot, same
 * day — because what changed was the commitment, not the placement.
 * Printing "Was X 09:00 / Now X 09:00" under that would make the surface
 * look broken and say nothing. Deterministic comparison of returned
 * facts; no inference about which change it was.
 */
export function placementMoved(
  previous: PlanChangeItem | null,
  replacement: PlanChangeItem | null,
): boolean {
  if (!previous || !replacement) return false
  return (
    previous.title !== replacement.title ||
    previous.timeSlot !== replacement.timeSlot ||
    previous.date !== replacement.date
  )
}

/**
 * The surface's own words. Deliberately small.
 *
 * `summary` counts rows the endpoint returned and says nothing else — no
 * urgency, no verdict, no invitation to fix anything. It is a label on a
 * closed drawer, and the drawer only opens when the user opens it (SC-13:
 * quiet by default, never mysterious).
 */
export const PLAN_CHANGE_COPY = {
  summary: (n: number) =>
    n === 1
      ? `${BRAND.assistant} adjusted one thing in today’s plan`
      : `${BRAND.assistant} adjusted ${n} things in today’s plan`,
  was: 'Was',
  now: 'Now',
} as const

/** Every static string this surface can render — the doctrine guard's input,
 *  mirroring `ALL_STATIC_STRINGS` for the page it hangs on. */
export const ALL_PLAN_CHANGE_STRINGS: string[] = [
  ...Object.values(REASON_COPY),
  ...Object.values(TRIGGER_COPY),
  displacedCopy('unscheduled') as string,
  displacedCopy('parked') as string,
  displacedCopy('schedule_block:42') as string,
  PLAN_CHANGE_COPY.summary(1),
  PLAN_CHANGE_COPY.summary(3),
  PLAN_CHANGE_COPY.was,
  PLAN_CHANGE_COPY.now,
]
